import { ArticleView, type AuthorInfo, batchGetAuthors } from "@serophin/nottldr-shared";
import { ArticleList } from "../../components/article/ArticleList";
import { AuthorProfile } from "../../components/author/AuthorProfile";
import { NotFoundPage } from "../NotFoundPage";
import type { PageProps } from "../pageProps";
import { type ArticleListSeed, type AuthorSeed, SEED } from "./seedTypes";
import { authorTabs, useHeaderTabs } from "./tabs";
import { useRemote } from "./useRemote";
import { useSeed } from "./useSeed";
import { useSeededArticles } from "./useSeededArticles";

const EMPTY_MESSAGE =
  "This author hasn't published any articles yet. Check back soon to see their content. In the mean time, you can explore our collection to discover interesting articles and authors!";

const TAB_CLASS = "pb-2 text-sm font-medium border-b-2 transition-colors";
const ACTIVE_TAB = "border-blue-600 text-blue-600 dark:text-blue-400";
const INACTIVE_TAB = "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300";

/** The author of the page: from the prerender, or looked up by alias. */
const useAuthor = (alias: string) => {
  const seed = useSeed<AuthorSeed>(SEED.author, (found) => found.author.alias.toLowerCase() === alias.toLowerCase());
  const author = useRemote<AuthorInfo>(seed?.author, alias, async () => (await batchGetAuthors([alias]))[0]);
  return { seed, author };
};

const DesktopTabs = ({ alias, active }: { alias: string; active: "profile" | "articles" }) => (
  <div class="hidden lg:flex gap-4 mb-4 border-b border-gray-200 dark:border-gray-700">
    {authorTabs(alias).map((tab) => (
      <a key={tab.id} href={tab.href} class={`${TAB_CLASS} ${tab.id === active ? ACTIVE_TAB : INACTIVE_TAB}`}>
        {tab.label}
      </a>
    ))}
  </div>
);

/** `/authors/:alias`: who the author is. */
export const AuthorPage = ({ alias = "" }: PageProps) => {
  const { author } = useAuthor(alias);
  useHeaderTabs(authorTabs(alias), "profile");

  if (author.status === "missing") {
    return <NotFoundPage />;
  }

  return (
    <div>
      <DesktopTabs alias={alias} active="profile" />
      {author.status === "ready" && <AuthorProfile author={author.data} />}
    </div>
  );
};

const AuthorArticles = ({ author, seed }: { author: AuthorInfo; seed?: ArticleListSeed }) => {
  const list = useSeededArticles({ view: ArticleView.AUTHOR, filter: { author: author.alias }, filterKey: author.alias, seed, limit: 20 });
  const authors = { [author.alias]: author, ...list.authors };

  return (
    <ArticleList
      metas={list.metas}
      authors={authors}
      hasMore={list.hasMore}
      isLoading={list.isLoading}
      onLoadMore={list.loadMore}
      emptyMessage={EMPTY_MESSAGE}
      showReadFilter
    />
  );
};

/** `/authors/:alias/articles`: everything the author has published. */
export const AuthorArticlesPage = ({ alias = "" }: PageProps) => {
  const { seed, author } = useAuthor(alias);
  useHeaderTabs(authorTabs(alias), "articles");

  if (author.status === "missing") {
    return <NotFoundPage />;
  }

  return (
    <div>
      <DesktopTabs alias={alias} active="articles" />
      {author.status === "ready" && <AuthorArticles key={author.data.alias} author={author.data} seed={seed?.list} />}
    </div>
  );
};
